/**
 * IntercomCallControls Component
 * 
 * Overlay that appears during an active intercom call.
 * Shows call controls: Push-to-Talk, Mute, End Call, mode toggle.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mic, MicOff, PhoneOff, Radio, 
  Phone, Volume2, Waves 
} from 'lucide-react';
import useIntercomCallStore from '../stores/intercomCallStore';

const IntercomCallControls = () => {
  const navigate = useNavigate();
  const {
    callState,
    activeIntercomName,
    isMuted,
    isPTTActive,
    pttMode,
    audioLevel,
    getFormattedDuration,
    endCall,
    toggleMute,
    startPTT,
    stopPTT,
    togglePTTMode
  } = useIntercomCallStore();

  // Keyboard shortcut: Space for PTT
  useEffect(() => {
    if (callState !== 'active' || !pttMode) return;

    const handleKeyDown = (e) => {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        startPTT();
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        stopPTT();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [callState, pttMode, startPTT, stopPTT]);

  // Don't render if no active call
  if (callState !== 'active' && callState !== 'connecting') return null;

  const formattedDuration = getFormattedDuration();
  const isConnecting = callState === 'connecting';
  const levelHeight = Math.min(audioLevel * 200, 100);

  return (
    <div className="intercom-call-controls">
      {/* Call info bar */}
      <div className="intercom-call-info">
        <div className="intercom-call-info-left">
          <Phone size={16} className="intercom-call-icon" />
          <span className="intercom-call-name">{activeIntercomName}</span>
        </div>
        <div className="intercom-call-info-right">
          {isConnecting ? (
            <span className="intercom-call-connecting">Connecting...</span>
          ) : (
            <span className="intercom-call-duration">{formattedDuration}</span>
          )}
        </div>
      </div>

      {/* Audio level indicator */}
      {callState === 'active' && (
        <div className="intercom-call-level-bar">
          <div 
            className="intercom-call-level-fill" 
            style={{ width: `${levelHeight}%` }}
          />
        </div>
      )}

      {/* Controls */}
      <div className="intercom-call-buttons">
        {/* Mode toggle: PTT vs Open Mic */}
        <button
          className={`intercom-call-btn intercom-call-btn-mode ${!pttMode ? 'active' : ''}`}
          onClick={togglePTTMode}
          title={pttMode ? 'Switch to Open Mic' : 'Switch to Push-to-Talk'}
          disabled={isConnecting}
        >
          {pttMode ? <Radio size={20} /> : <Waves size={20} />}
          <span className="intercom-call-btn-label">
            {pttMode ? 'PTT' : 'Open'}
          </span>
        </button>

        {/* Push-to-Talk / Transmit indicator */}
        {pttMode ? (
          <button
            className={`intercom-call-btn intercom-call-btn-ptt ${isPTTActive ? 'transmitting' : ''}`}
            onMouseDown={startPTT}
            onMouseUp={stopPTT}
            onMouseLeave={stopPTT}
            onTouchStart={(e) => { e.preventDefault(); startPTT(); }}
            onTouchEnd={(e) => { e.preventDefault(); stopPTT(); }}
            onTouchCancel={stopPTT}
            disabled={isConnecting || isMuted}
            title="Hold to talk (or press Space)"
          >
            <Mic size={24} />
            <span className="intercom-call-btn-label">
              {isPTTActive ? 'Talking...' : 'Hold to Talk'}
            </span>
          </button>
        ) : (
          <div className={`intercom-call-btn intercom-call-btn-open-mic ${isMuted ? 'muted' : 'transmitting'}`}>
            <Volume2 size={24} />
            <span className="intercom-call-btn-label">
              {isMuted ? 'Muted' : 'Live Mic'}
            </span>
          </div>
        )}

        {/* Mute toggle */}
        <button
          className={`intercom-call-btn intercom-call-btn-mute ${isMuted ? 'active' : ''}`}
          onClick={toggleMute}
          disabled={isConnecting}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? <MicOff size={20} /> : <Mic size={20} />}
          <span className="intercom-call-btn-label">
            {isMuted ? 'Unmute' : 'Mute'}
          </span>
        </button>
      </div>

      {/* PTT hint */}
      {pttMode && callState === 'active' && (
        <div className="intercom-call-hint">
          Press <kbd>Space</kbd> or hold the mic button to talk
        </div>
      )}
    </div>
  );
};

export default IntercomCallControls;
